import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  deleteUser,
  fetchUserAttributes,
  updateUserAttributes,
} from 'aws-amplify/auth';
import { useAuth } from '../../auth/useAuth.ts';
import { humanizeAuthError } from '../../auth/errors.ts';
import {
  CONSENT_DOCS,
  grantConsent,
  parseConsents,
  revokeConsent,
  type ConsentKey,
  type Consents,
} from '../../auth/useConsent.ts';
import ConfirmDialog from '../../components/ui/ConfirmDialog.tsx';
import {
  confirmSmsVerification,
  removeSmsVerification,
  startSmsVerification,
} from '../../agents/smsApi.ts';
import { SMS_FEATURE_ENABLED, SUBSCRIPTIONS_ENABLED } from '../../config/features.ts';
import AccountProfileSection from './AccountProfileSection.tsx';
import AccountSubscriptionSection from './AccountSubscriptionSection.tsx';
import AccountMcpSection from './AccountMcpSection.tsx';
import { useBillingReturn } from './useBillingReturn.tsx';
import { emailAgentContact, smsAgentContact } from '../../agents/agentContact.ts';
import { formatE164ForDisplay, isValidE164, seedPlusOne } from '../../lib/phone.ts';
import { buildVCard, vCardDataUri } from '../../lib/vcard.ts';
import './Account.css';

type DeletePhase = 'idle' | 'confirming' | 'deleting';
const CONFIRM_PHRASE = 'DELETE';

// The "Text my agent" (SMS) section shows in the deployed stages only — flag
// centralized in config/features.ts (U19); rationale documented there. The
// component tests mock the stage to exercise both branches.

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

export default function Account() {
  const { user, signOut, refresh, status } = useAuth();
  const navigate = useNavigate();
  const billingBanner = useBillingReturn(refresh);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletePhase, setDeletePhase] = useState<DeletePhase>('idle');
  const [deleteConfirm, setDeleteConfirm] = useState('');

  // Agreement consent (Cognito `custom:consents`). null = still loading.
  const [consents, setConsents] = useState<Consents | null>(null);
  const [consentBusy, setConsentBusy] = useState<ConsentKey | null>(null);
  const [withdrawTarget, setWithdrawTarget] = useState<ConsentKey | null>(null);

  // Email-to-agent toggle (Cognito `custom:agentEmailOptOut`). null = loading.
  // Absent / not "true" = enabled (default-on; opting out sets "true").
  const [agentEmailEnabled, setAgentEmailEnabled] = useState<boolean | null>(null);
  const [agentEmailBusy, setAgentEmailBusy] = useState(false);

  // Text-my-agent (Cognito `phone_number` + `custom:smsVerified` + `custom:smsOptOut`).
  // Stricter than email: requires explicit verified opt-in (no default-on).
  // `smsVerified === null` = still loading. The verify wizard runs the OTP flow.
  const [smsVerified, setSmsVerified] = useState<boolean | null>(null);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsWizard, setSmsWizard] = useState<'idle' | 'phone' | 'code'>('idle');
  const [smsPhone, setSmsPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsError, setSmsError] = useState<string | null>(null);
  // Per-A2P-review opt-in: both boxes must be ticked before a number can be
  // submitted. Reset whenever the phone step is (re-)entered or cancelled so a
  // fresh number always re-consents.
  const [smsConsentMessaging, setSmsConsentMessaging] = useState(false);
  const [smsConsentTerms, setSmsConsentTerms] = useState(false);
  // Whether the phone field has been blurred — gates the inline invalid state so
  // we don't flag a number the user is still mid-typing.
  const [smsPhoneTouched, setSmsPhoneTouched] = useState(false);
  // Remove/unlink confirmation modal (clears the number + opts out server-side).
  const [smsRemoveOpen, setSmsRemoveOpen] = useState(false);
  // OTP input, focused the moment the code step opens (paired with hiding the
  // rest of the page) so the user lands straight on it.
  const smsCodeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const cancelled = { current: false };
    const isCancelled = (): boolean => cancelled.current;
    void (async () => {
      try {
        const attrs = await fetchUserAttributes();
        if (!isCancelled()) {
          setConsents(parseConsents(attrs['custom:consents']));
          // Default-on: only an explicit "true" disables it.
          setAgentEmailEnabled(attrs['custom:agentEmailOptOut'] !== 'true');
          const verified = attrs['custom:smsVerified'] === 'true';
          setSmsVerified(verified);
          setSmsEnabled(verified && attrs['custom:smsOptOut'] !== 'true');
          setSmsPhone(attrs.phone_number ?? '');
        }
      } catch {
        // Degrade to "nothing recorded" rather than blocking the page.
        if (!isCancelled()) {
          setConsents({});
          setAgentEmailEnabled(true);
          setSmsVerified(false);
          setSmsEnabled(false);
        }
      }
    })();
    return () => {
      cancelled.current = true;
    };
  }, []);

  // Deep-link support: the agent tour + marketing link to /app/account#channels.
  // React Router doesn't scroll to hash anchors, so do it once on mount.
  useEffect(() => {
    if (window.location.hash !== '#channels') {
      return;
    }
    // Defer to after the sections render.
    const id = window.setTimeout(() => {
      document.getElementById('channels')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
    return () => window.clearTimeout(id);
  }, []);

  // When the OTP step opens, move focus to the code input so the user is placed
  // directly on it (the rest of the page is hidden while this step is active).
  useEffect(() => {
    if (smsWizard === 'code') {
      smsCodeRef.current?.focus();
    }
  }, [smsWizard]);

  if (status === 'initializing') {
    return null;
  }

  async function handleSignOutAll() {
    setError(null);
    setBusy(true);
    try {
      await signOut();
      navigate('/auth/signed-out', { replace: true });
    } catch (err) {
      setError(humanizeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    setError(null);
    setDeletePhase('deleting');
    try {
      // Cognito's self-service `DeleteUser` API — authorised via the
      // user's own access token. Amplify v6 deliberately leaves the
      // local session in place, so we chain signOut() to clear tokens;
      // the GlobalSignOut wire-call inside it may fail because the user
      // no longer exists, hence the swallowed catch. refresh() then
      // pushes AuthContext to 'unauthenticated' before navigating,
      // otherwise the route guard races us.
      await deleteUser();
      try {
        await signOut();
      } catch {
        /* user is gone server-side; local-cleanup still succeeded */
      }
      await refresh();
      navigate('/auth/signed-out', { replace: true, state: { deleted: true } });
    } catch (err) {
      setError(humanizeAuthError(err));
      setDeletePhase('confirming');
    }
  }

  // Persist a new consents blob to Cognito and reflect it locally.
  async function persistConsents(next: Consents, key: ConsentKey) {
    setError(null);
    setConsentBusy(key);
    try {
      await updateUserAttributes({
        userAttributes: { 'custom:consents': JSON.stringify(next) },
      });
      setConsents(next);
    } catch (err) {
      setError(humanizeAuthError(err));
    } finally {
      setConsentBusy(null);
    }
  }

  function handleReaccept(key: ConsentKey) {
    void persistConsents(grantConsent(consents ?? {}, key), key);
  }

  // Persist the email-to-agent toggle (opt-out attribute) and reflect locally.
  async function setAgentEmail(enabled: boolean) {
    setError(null);
    setAgentEmailBusy(true);
    try {
      await updateUserAttributes({
        userAttributes: { 'custom:agentEmailOptOut': enabled ? 'false' : 'true' },
      });
      setAgentEmailEnabled(enabled);
    } catch (err) {
      setError(humanizeAuthError(err));
    } finally {
      setAgentEmailBusy(false);
    }
  }

  // --- Text my agent (SMS) -------------------------------------------------
  async function handleSmsStart() {
    setSmsError(null);
    setSmsBusy(true);
    try {
      await startSmsVerification(smsPhone.trim());
      setSmsWizard('code');
    } catch (err) {
      setSmsError(err instanceof Error ? err.message : 'Could not send a verification code.');
    } finally {
      setSmsBusy(false);
    }
  }

  async function handleSmsConfirm() {
    setSmsError(null);
    setSmsBusy(true);
    try {
      await confirmSmsVerification(smsPhone.trim(), smsCode.trim());
      // Server-set the verified state; reflect locally. A welcome text follows.
      setSmsVerified(true);
      setSmsEnabled(true);
      setSmsWizard('idle');
      setSmsCode('');
      setSmsConsentMessaging(false);
      setSmsConsentTerms(false);
      setSmsPhoneTouched(false);
    } catch (err) {
      setSmsError(err instanceof Error ? err.message : 'That code didn’t match.');
    } finally {
      setSmsBusy(false);
    }
  }

  // Pause/resume an already-verified number via the opt-out attribute.
  async function setSmsOptedIn(enabled: boolean) {
    setSmsError(null);
    setSmsBusy(true);
    try {
      await updateUserAttributes({
        userAttributes: { 'custom:smsOptOut': enabled ? 'false' : 'true' },
      });
      setSmsEnabled(enabled);
    } catch (err) {
      setSmsError(humanizeAuthError(err));
    } finally {
      setSmsBusy(false);
    }
  }

  // Unlink the number entirely (server clears phone_number + revokes verified +
  // opts out). Returns the section to the "Add your phone" state; a new number
  // is re-added via the ordinary verify wizard. Keeps the modal open on failure
  // so the error is visible.
  async function handleSmsRemove() {
    setSmsError(null);
    setSmsBusy(true);
    try {
      await removeSmsVerification();
      setSmsVerified(false);
      setSmsEnabled(false);
      setSmsPhone('');
      setSmsPhoneTouched(false);
      setSmsWizard('idle');
      setSmsCode('');
      setSmsRemoveOpen(false);
    } catch (err) {
      setSmsError(err instanceof Error ? err.message : 'Could not remove your number.');
    } finally {
      setSmsBusy(false);
    }
  }

  function handleWithdrawConfirm() {
    const key = withdrawTarget;
    setWithdrawTarget(null);
    if (!key) {
      return;
    }
    if (key === 'ai') {
      // AI consent is genuinely revocable in-product.
      void persistConsents(revokeConsent(consents ?? {}, key), key);
    } else {
      // Terms / Privacy can't be used without — withdrawing means leaving the
      // service, so route into the existing account-deletion flow.
      setDeletePhase('confirming');
    }
  }

  const withdrawDoc = withdrawTarget
    ? CONSENT_DOCS.find((d) => d.key === withdrawTarget)
    : undefined;
  const isAiWithdraw = withdrawTarget === 'ai';

  // Non-empty but not a valid E.164 number. Drives the inline error once blurred.
  const smsPhoneInvalid = smsPhone.trim() !== '' && !isValidE164(smsPhone);
  const showSmsPhoneError = smsPhoneTouched && smsPhoneInvalid;
  // While the OTP step is open, collapse the rest of the page to just the code
  // entry so the user doesn't drift off to another section mid-verification.
  const awaitingSmsCode = SMS_FEATURE_ENABLED && smsWizard === 'code';

  // "Add to contacts" download for the SMS agent (email + number, stage-correct).
  const agentVCardHref = vCardDataUri(
    buildVCard({
      name: smsAgentContact.name,
      org: 'OculusMind.AI',
      title: 'AI agent',
      email: smsAgentContact.email,
      tel: smsAgentContact.smsNumber,
      url: smsAgentContact.profileUrl,
    }),
  );

  return (
    <div className="om-account">
      <header className="om-account__header">
        <h1 className="om-account__title">Account</h1>
        <Link to="/app" className="om-account__back">
          ← Back to app
        </Link>
      </header>

      {billingBanner}

      <AccountProfileSection user={user} refresh={refresh} hidden={awaitingSmsCode} />

      <AccountMcpSection user={user} hidden={awaitingSmsCode} />

      {SUBSCRIPTIONS_ENABLED ? (
        <AccountSubscriptionSection user={user} hidden={awaitingSmsCode} />
      ) : null}

      <section className="om-account__section" hidden={awaitingSmsCode}>
        <h2 className="om-account__h2">Email my agents</h2>
        <p className="om-account__pending">
          When on, you can email your agents directly (e.g.{' '}
          <code>{emailAgentContact.email}</code>) and they'll reply by email,
          acting on your behalf. Turn it off to disable inbound email handling
          for your account.
        </p>
        {agentEmailEnabled === null ? (
          <p className="om-account__pending">Loading…</p>
        ) : (
          <>
            <div className="om-account__consent-row">
              <div className="om-account__consent-doc">
                <span>Email-to-agent</span>
                <span
                  className={`om-account__badge om-account__badge--${
                    agentEmailEnabled ? 'on' : 'off'
                  }`}
                >
                  {agentEmailEnabled ? 'On' : 'Off'}
                </span>
              </div>
              <button
                type="button"
                className={
                  agentEmailEnabled
                    ? 'om-account__action om-account__action--warning'
                    : 'om-account__action'
                }
                onClick={() => void setAgentEmail(!agentEmailEnabled)}
                disabled={agentEmailBusy}
              >
                {agentEmailBusy
                  ? 'Saving…'
                  : agentEmailEnabled
                    ? 'Turn off'
                    : 'Turn on'}
              </button>
            </div>
            {agentEmailEnabled ? (
              <p className="om-account__channel-link">
                Email {emailAgentContact.name} at{' '}
                <a href={`mailto:${emailAgentContact.email}`}>{emailAgentContact.email}</a>.
              </p>
            ) : null}
          </>
        )}
      </section>

      {SMS_FEATURE_ENABLED && (
        <section className="om-account__section" id="channels">
        <h2 className="om-account__h2">Text my agents</h2>
        <p className="om-account__pending">
          Verify a phone number to text your agent (e.g. reminders, quick
          questions) and get replies by SMS. Carrier message &amp; data rates may
          apply; reply STOP anytime to opt out.
        </p>
        {smsError !== null ? (
          <p className="om-account__error" role="alert">
            {smsError}
          </p>
        ) : null}
        {smsVerified === null ? (
          <p className="om-account__pending">Loading…</p>
        ) : smsVerified ? (
          <>
            <div className="om-account__consent-row">
              <div className="om-account__consent-doc">
                <span>Text-to-agent{smsPhone ? ` (${smsPhone})` : ''}</span>
                <span
                  className={`om-account__badge om-account__badge--${
                    smsEnabled ? 'on' : 'off'
                  }`}
                >
                  {smsEnabled ? 'On' : 'Off'}
                </span>
              </div>
              <div className="om-account__sms-actions">
                <button
                  type="button"
                  className="om-account__action om-account__action--danger"
                  onClick={() => {
                    setSmsError(null);
                    setSmsRemoveOpen(true);
                  }}
                  disabled={smsBusy}
                >
                  Remove
                </button>
                <button
                  type="button"
                  className={
                    smsEnabled
                      ? 'om-account__action om-account__action--warning'
                      : 'om-account__action'
                  }
                  onClick={() => void setSmsOptedIn(!smsEnabled)}
                  disabled={smsBusy}
                >
                  {smsBusy ? 'Saving…' : smsEnabled ? 'Turn off' : 'Turn on'}
                </button>
              </div>
            </div>
            {smsEnabled ? (
              <div className="om-account__contact-pills">
                <a className="om-account__contact-pill" href={`sms:${smsAgentContact.smsNumber}`}>
                  Text {smsAgentContact.name}
                  <span className="om-account__contact-pill-number">
                    {formatE164ForDisplay(smsAgentContact.smsNumber)}
                  </span>
                </a>
                <a
                  className="om-account__contact-pill"
                  href={agentVCardHref}
                  download={`${smsAgentContact.slug}.vcf`}
                >
                  Add to contacts
                </a>
              </div>
            ) : null}
          </>
        ) : smsWizard === 'idle' ? (
          <button
            type="button"
            className="om-account__action"
            onClick={() => {
              setSmsError(null);
              setSmsConsentMessaging(false);
              setSmsConsentTerms(false);
              setSmsPhoneTouched(false);
              setSmsWizard('phone');
            }}
          >
            Add your phone
          </button>
        ) : smsWizard === 'phone' ? (
          <div className="om-account__sms-wizard">
            <p className="om-account__sms-intro">
              Sign up to interact with your AI assistants over SMS messaging.
            </p>
            <label className="om-account__sms-label" htmlFor="sms-phone">
              Phone number (with country code)
            </label>
            <input
              id="sms-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+1 (555) 123-4567"
              className={
                showSmsPhoneError
                  ? 'om-account__input om-account__input--error'
                  : 'om-account__input'
              }
              value={smsPhone}
              onChange={(e) => setSmsPhone(seedPlusOne(smsPhone, e.target.value))}
              onBlur={() => setSmsPhoneTouched(true)}
              disabled={smsBusy}
              aria-invalid={showSmsPhoneError}
              aria-describedby="sms-phone-hint"
            />
            {showSmsPhoneError ? (
              <p id="sms-phone-hint" className="om-account__field-error" role="alert">
                Enter your number as + [country code] [phone number] — e.g. +1 (555)
                123-4567.
              </p>
            ) : (
              <p id="sms-phone-hint" className="om-account__sms-hint">
                Include your country code — U.S. numbers start with +1.
              </p>
            )}
            <label className="om-account__sms-check">
              <input
                type="checkbox"
                checked={smsConsentMessaging}
                onChange={(e) => setSmsConsentMessaging(e.target.checked)}
                disabled={smsBusy}
              />
              <span>
                By checking, you consent to receive SMS messages from your AI
                agents in response to your requests, from Andrew Paris LLC,
                d/b/a MediaShare-Link, operator of the OculusMind.AI platform.
                Message frequency may vary, but is only in
                response to your requests. Message and data rates may apply.
                Reply STOP to opt-out.
              </span>
            </label>
            <label className="om-account__sms-check">
              <input
                type="checkbox"
                checked={smsConsentTerms}
                onChange={(e) => setSmsConsentTerms(e.target.checked)}
                disabled={smsBusy}
              />
              <span>
                By checking, I accept{' '}
                <Link to="/legal/terms" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </Link>{' '}
                &amp;{' '}
                <Link
                  to="/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
                .
              </span>
            </label>
            <div className="om-account__sms-actions">
              <button
                type="button"
                className="om-account__action"
                onClick={() => void handleSmsStart()}
                disabled={
                  smsBusy ||
                  !isValidE164(smsPhone) ||
                  !smsConsentMessaging ||
                  !smsConsentTerms
                }
              >
                {smsBusy ? 'Sending…' : 'Send code'}
              </button>
              <button
                type="button"
                className="om-account__action om-account__action--ghost"
                onClick={() => {
                  setSmsConsentMessaging(false);
                  setSmsConsentTerms(false);
                  setSmsPhoneTouched(false);
                  setSmsWizard('idle');
                }}
                disabled={smsBusy}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="om-account__sms-wizard">
            <label className="om-account__sms-label" htmlFor="sms-code">
              Enter the 6-digit code we texted to {smsPhone}
            </label>
            <input
              ref={smsCodeRef}
              id="sms-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              className="om-account__input"
              value={smsCode}
              onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
              disabled={smsBusy}
            />
            <div className="om-account__sms-actions">
              <button
                type="button"
                className="om-account__action"
                onClick={() => void handleSmsConfirm()}
                disabled={smsBusy || smsCode.trim().length !== 6}
              >
                {smsBusy ? 'Verifying…' : 'Verify'}
              </button>
              <button
                type="button"
                className="om-account__action om-account__action--ghost"
                onClick={() => void handleSmsStart()}
                disabled={smsBusy}
              >
                Resend code
              </button>
            </div>
          </div>
        )}
        </section>
      )}

      <section className="om-account__section" hidden={awaitingSmsCode}>
        <h2 className="om-account__h2">Consent</h2>
        <p className="om-account__pending">
          The agreements you accepted when you created your account. Withdrawing
          the Terms of Service or Privacy Policy requires deleting your account,
          since the Services cannot be provided without them.
        </p>
        {consents === null ? (
          <p className="om-account__pending">Loading…</p>
        ) : (
          <ul className="om-account__consents">
            {CONSENT_DOCS.map((doc) => {
              const record = consents[doc.key];
              const revoked = record?.revoked;
              return (
                <li key={doc.key} className="om-account__consent-row">
                  <div className="om-account__consent-doc">
                    <Link to={doc.path}>{doc.label}</Link>
                    <span className="om-account__consent-status">
                      {record == null
                        ? 'Not recorded'
                        : revoked
                          ? `Revoked ${formatDate(revoked)}`
                          : `Accepted ${record.v} · ${formatDate(record.at)}`}
                    </span>
                  </div>
                  {record != null && revoked ? (
                    <button
                      type="button"
                      className="om-account__action"
                      onClick={() => handleReaccept(doc.key)}
                      disabled={consentBusy === doc.key}
                    >
                      {consentBusy === doc.key ? 'Saving…' : 'Re-accept'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="om-account__action om-account__action--warning"
                      onClick={() => setWithdrawTarget(doc.key)}
                      disabled={consentBusy === doc.key}
                    >
                      {consentBusy === doc.key ? 'Saving…' : 'Withdraw'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="om-account__section" hidden={awaitingSmsCode}>
        <h2 className="om-account__h2">Cookies &amp; Tracking</h2>
        <p className="om-account__pending">
          Cookie and tracking consent is managed separately from the account
          agreements above, through our consent management platform. Review or
          withdraw your choices any time:
        </p>
        <Link
          to="/legal/cookie-preferences"
          className="om-account__action om-account__action--link"
        >
          Cookie Preferences
        </Link>
      </section>

      <section className="om-account__section" hidden={awaitingSmsCode}>
        <h2 className="om-account__h2">Delete account</h2>
        <p className="om-account__pending">
          Permanently delete your OculusMind.AI account. This signs you out
          everywhere, removes your profile and credentials from our identity
          provider, and cannot be undone.
        </p>
        {deletePhase === 'idle' ? (
          <button
            type="button"
            className="om-account__action om-account__action--danger"
            onClick={() => setDeletePhase('confirming')}
          >
            Delete account
          </button>
        ) : (
          <div className="om-account__confirm">
            <p className="om-account__confirm-text">
              Type <strong>{CONFIRM_PHRASE}</strong> to confirm. This cannot be
              undone.
            </p>
            <input
              type="text"
              className="om-account__confirm-input"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              disabled={deletePhase === 'deleting'}
              aria-label={`Type ${CONFIRM_PHRASE} to confirm account deletion`}
            />
            <div className="om-account__confirm-actions">
              <button
                type="button"
                className="om-account__action om-account__action--danger"
                onClick={() => {
                  void handleDelete();
                }}
                disabled={
                  deleteConfirm !== CONFIRM_PHRASE || deletePhase === 'deleting'
                }
              >
                {deletePhase === 'deleting'
                  ? 'Deleting…'
                  : 'Permanently delete'}
              </button>
              <button
                type="button"
                className="om-account__action"
                onClick={() => {
                  setDeletePhase('idle');
                  setDeleteConfirm('');
                  setError(null);
                }}
                disabled={deletePhase === 'deleting'}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="om-account__section" hidden={awaitingSmsCode}>
        <h2 className="om-account__h2">Security</h2>
        <p className="om-account__pending">
          To change your password, use the &ldquo;Forgot password?&rdquo; link on
          the <Link to="/auth/signin">sign-in page</Link>. Multi-factor
          authentication preference can&rsquo;t be changed here; to use a
          different MFA setup, create a new account under a different email
          address. You can also revoke all active sessions:
        </p>
        <button
          type="button"
          className="om-account__action om-account__action--warning"
          onClick={() => {
            void handleSignOutAll();
          }}
          disabled={busy}
        >
          {busy ? 'Signing out…' : 'Sign out everywhere'}
        </button>
      </section>

      {error ? (
        <p className="om-account__error" role="alert">
          {error}
        </p>
      ) : null}

      <ConfirmDialog
        open={withdrawTarget !== null}
        title={
          isAiWithdraw
            ? 'Withdraw AI consent'
            : `Withdraw ${withdrawDoc?.label ?? 'consent'}`
        }
        message={
          isAiWithdraw
            ? 'This records that you have withdrawn your consent to AI processing. Core features of OculusMind.AI depend on AI; you can re-accept at any time from this page.'
            : `The ${withdrawDoc?.label ?? 'agreement'} is required to use OculusMind.AI. Withdrawing it means you can no longer use your account — to proceed you must delete your account.`
        }
        confirmLabel={
          isAiWithdraw ? 'Withdraw consent' : 'Continue to delete account'
        }
        danger
        onConfirm={handleWithdrawConfirm}
        onCancel={() => setWithdrawTarget(null)}
      />

      <ConfirmDialog
        open={smsRemoveOpen}
        title="Remove phone number"
        message={
          smsPhone
            ? `This unlinks ${smsPhone} and turns off texting your agent. You can add a number again anytime.`
            : 'This unlinks your number and turns off texting your agent. You can add a number again anytime.'
        }
        confirmLabel="Remove number"
        danger
        busy={smsBusy}
        error={smsError}
        onConfirm={() => void handleSmsRemove()}
        onCancel={() => {
          setSmsRemoveOpen(false);
          setSmsError(null);
        }}
      />
    </div>
  );
}
